/**
 * YÊU CẦU — nhân viên gửi đề nghị cho quản lý xử lý.
 *
 * Hai tab theo bản thiết kế `fe/yeu-cau-don-tu.pdf`:
 *   · Tạo mới đơn từ    — danh sách loại đơn, chọn một loại rồi điền biểu mẫu
 *   · Lịch sử tạo đơn từ — đơn đã gửi và kết quả xử lý
 *
 * Danh sách loại đơn và phép ánh xạ sang mã server nằm ở
 * `services/request.service.ts`. **Đọc đầu tệp đó trước khi sửa màn này** —
 * bản thiết kế vẽ năm loại đơn mà server mới nhận ba, và cách xử lý khoảng
 * chênh đó là phần quan trọng nhất của màn hình này.
 */

import { useMemo, useState } from 'react'

import { useTaiDuLieu } from '../hooks/useTaiDuLieu'
import { gio, ngay as dinhDangNgay, ngayCong } from '../lib/gio'
import { chuCaiDau } from '../lib/ten'
import { layDeviceId } from '../lib/thietbi'
import { uuidV4 } from '../lib/uuid'
import { xinGanThietBi } from '../services/auth.service'
import { layDiaDiem } from '../services/catalog.service'
import {
  LOAI_DON, guiDon, layBanGhiTrongNgay, layDonDaGui, nhanLoaiDon,
  type LoaiDon,
} from '../services/request.service'
import type { DiaDiem } from '../types'
import { HopLoi, Icon, OTrong, XuongDong, useThongBao } from '../ui'

type Tab = 'tao-moi' | 'lich-su'

const NHAN_TRANG_THAI: Record<string, { nhan: string; lop: string }> = {
  pending: { nhan: 'Chờ xử lý', lop: 'cho' },
  approved: { nhan: 'Đã duyệt', lop: 'tot' },
  rejected: { nhan: 'Đã từ chối', lop: 'tu-choi' },
}

export default function YeuCau() {
  const [tab, setTab] = useState<Tab>('tao-moi')
  const [dangChon, setDangChon] = useState<LoaiDon | null>(null)

  if (dangChon) {
    return (
      <FormDon
        loai={dangChon}
        quayLai={() => setDangChon(null)}
        xong={() => { setDangChon(null); setTab('lich-su') }}
      />
    )
  }

  return (
    <div className="man-hinh" style={{ display: 'grid', gap: 'var(--k4)' }}>
      <nav className="tab-phu" aria-label="Chế độ xem">
        <button type="button"
                className={`tab-phu-muc ${tab === 'tao-moi' ? 'dang-chon' : ''}`}
                onClick={() => setTab('tao-moi')}>
          Tạo mới đơn từ
        </button>
        <button type="button"
                className={`tab-phu-muc ${tab === 'lich-su' ? 'dang-chon' : ''}`}
                onClick={() => setTab('lich-su')}>
          Lịch sử tạo đơn từ
        </button>
      </nav>

      {tab === 'tao-moi'
        ? <DanhSachLoai chon={setDangChon} />
        : <LichSuDon />}
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
 * DANH SÁCH LOẠI ĐƠN
 * ═══════════════════════════════════════════════════════════════════════════ */

const ICON_LOAI = {
  'dong-ho': 'dong-ho',
  'nguoi-dung': 'nguoi-dung',
  sao: 'sao',
  trang: 'trang',
  'doi-chieu': 'doi-chieu',
} as const

function DanhSachLoai({ chon }: { chon: (l: LoaiDon) => void }) {
  const chuaCo = LOAI_DON.filter((l) => l.ma_server === null).length

  return (
    <>
      <p className="mo-ta-trang" style={{ margin: 0 }}>
        Gửi đề nghị để quản lý xử lý. Bản ghi chấm công không sửa trực tiếp
        được — mọi thay đổi đều tạo bản đính chính và lưu vết.
      </p>

      <div style={{ display: 'grid', gap: 'var(--k3)' }}>
        {LOAI_DON.map((l) => {
          const dungDuoc = l.ma_server !== null
          return (
            <button
              key={l.khoa}
              type="button"
              className="the-loai-don"
              disabled={!dungDuoc}
              onClick={() => dungDuoc && chon(l)}
            >
              <span className="loai-don-icon" style={{ background: l.mau }}>
                <Icon ten={ICON_LOAI[l.icon]} size={24} />
              </span>
              <span>
                <span className="loai-don-ten">{l.nhan}</span>
                <span className="loai-don-mo-ta">{l.mo_ta}</span>
                {/* Nói thẳng trên thẻ. Để người dùng bấm vào rồi mới gặp lỗi
                    422 là cách chắc chắn nhất khiến họ nghĩ app hỏng. */}
                {!dungDuoc && (
                  <span className="loai-don-chua-co">Hệ thống chưa hỗ trợ</span>
                )}
              </span>
            </button>
          )
        })}
      </div>

      {chuaCo > 0 && (
        <div className="the-khoi">
          <p style={{ margin: 0, fontSize: 'var(--cy-nho)', color: 'var(--chu-phu)' }}>
            <strong>{chuaCo} loại đơn</strong> phía trên đang bị mờ vì hệ thống
            chưa làm phần đó. Nghỉ phép, làm thêm và đổi ca hiện xử lý qua kênh
            nội bộ của công ty. Nếu việc đó làm bảng công của bạn thiếu ngày,
            hãy dùng <strong>Đơn checkin/out</strong> hoặc{' '}
            <strong>Đơn đính chính</strong> để quản lý bổ sung.
          </p>
        </div>
      )}
    </>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
 * LỊCH SỬ ĐƠN
 * ═══════════════════════════════════════════════════════════════════════════ */

function LichSuDon() {
  const { du_lieu, dang_tai, loi, tai_lai } = useTaiDuLieu(() => layDonDaGui())
  const [hienTatCa, setHienTatCa] = useState(false)

  const ds = du_lieu?.items ?? []
  const hienThi = hienTatCa ? ds : ds.slice(0, 4)

  if (dang_tai && !du_lieu) return <XuongDong so={3} cao />
  if (loi) return <HopLoi loi={loi} thuLai={tai_lai} />

  if (ds.length === 0) {
    return (
      <div className="the-khoi">
        <OTrong
          icon="soan"
          tieu_de="Bạn chưa gửi đơn nào"
          mo_ta="Đơn bạn gửi và kết quả quản lý xử lý sẽ hiện ở đây."
        />
      </div>
    )
  }

  return (
    <>
      <p style={{ margin: 0, color: 'var(--chu-phu)', fontSize: 'var(--cy-nho)' }}>
        Tất cả ({ds.length})
      </p>

      <div style={{ display: 'grid', gap: 'var(--k3)' }}>
        {hienThi.map((d) => {
          const tt = NHAN_TRANG_THAI[d.status] ?? { nhan: d.status, lop: '' }
          return (
            <article key={d.id} className="the-lich-su-don">
              <div className="don-dau">
                <span className="don-chu-cai" aria-hidden="true">
                  {chuCaiDau(nhanLoaiDon(d.type))}
                </span>
                <span style={{ minWidth: 0 }}>
                  <span className="don-ten">{nhanLoaiDon(d.type)}</span>
                  <span className="don-gio" style={{ display: 'block' }}>
                    Ngày {dinhDangNgay(`${d.work_date}T00:00:00`)}
                  </span>
                </span>
                <span className={`the-nhan ${tt.lop}`}>{tt.nhan}</span>
              </div>

              {d.reason && (
                <div className="don-than">
                  <span className="nhan">Lý do</span>
                  <span className="gia-tri">{d.reason}</span>
                </div>
              )}

              {d.decision_reason && (
                <div className="don-than">
                  <span className="nhan">Quản lý trả lời</span>
                  <span className="gia-tri">{d.decision_reason}</span>
                </div>
              )}
            </article>
          )
        })}
      </div>

      {ds.length > 4 && !hienTatCa && (
        <button type="button" className="lien-ket-phu"
                onClick={() => setHienTatCa(true)}>
          Xem thêm ({ds.length - 4})
        </button>
      )}
    </>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
 * BIỂU MẪU
 * ═══════════════════════════════════════════════════════════════════════════ */

function FormDon(
  { loai, quayLai, xong }: {
    loai: LoaiDon
    quayLai: () => void
    xong: () => void
  },
) {
  const tb = useThongBao()
  const homNay = ngayCong()

  const [ngayLienQuan, setNgayLienQuan] = useState(homNay)
  const [gioDeNghi, setGioDeNghi] = useState('08:00')
  const [chieu, setChieu] = useState<'in' | 'out'>('in')
  const [banGhiId, setBanGhiId] = useState('')
  const [diaDiemId, setDiaDiemId] = useState('')
  const [lyDo, setLyDo] = useState('')

  const [loiTruong, setLoiTruong] = useState<Record<string, string>>({})
  const [dangGui, setDangGui] = useState(false)
  const [daGui, setDaGui] = useState(false)

  /* `client_uuid` sinh MỘT LẦN khi mở biểu mẫu và giữ nguyên qua mọi lần thử
     lại — giống hệt nguyên tắc của hàng đợi chấm công. Bấm gửi hai lần vì
     mạng chậm vẫn chỉ ra một đơn: server có `ON CONFLICT DO NOTHING` trên
     (employee_id, client_uuid). */
  const [clientUuid] = useState(() => uuidV4())

  const laDoiThietBi = loai.khoa === 'doi_thiet_bi'

  const { du_lieu: dsDiaDiem } = useTaiDuLieu(
    () => layDiaDiem(), [], { bat: !laDoiThietBi })

  const { du_lieu: dsBanGhi } = useTaiDuLieu(
    () => layBanGhiTrongNgay(ngayLienQuan),
    [ngayLienQuan],
    { bat: loai.can_ban_ghi },
  )

  const diaDiem: DiaDiem[] = dsDiaDiem ?? []
  const banGhi = useMemo(
    () => (dsBanGhi?.items ?? []).filter((r) => r.status !== 'corrected'),
    [dsBanGhi],
  )

  function kiemTra(): boolean {
    const l: Record<string, string> = {}

    if (lyDo.trim().length < 5) {
      l.lyDo = 'Cần mô tả ít nhất 5 ký tự để quản lý hiểu và xử lý nhanh.'
    }
    if (!laDoiThietBi && ngayLienQuan > homNay) {
      l.ngay = 'Không đề nghị cho ngày ở tương lai.'
    }
    if (loai.can_ban_ghi && !banGhiId) {
      l.banGhi = 'Chọn bản ghi bạn muốn đính chính.'
    }
    if (loai.can_gio && !laDoiThietBi) {
      // Server cũng chặn, nhưng bắt ở đây thì người dùng không mất một vòng
      // gửi đi gửi lại trên mạng 3G ở công trường.
      const moc = new Date(`${ngayLienQuan}T${gioDeNghi}:00+07:00`)
      if (moc.getTime() > Date.now()) {
        l.gio = 'Giờ đề nghị đang ở tương lai. Kiểm tra lại ngày và giờ.'
      }
    }

    setLoiTruong(l)
    return Object.keys(l).length === 0
  }

  async function gui(e: React.FormEvent) {
    e.preventDefault()
    if (dangGui || daGui || !kiemTra()) return

    setDangGui(true)
    try {
      if (laDoiThietBi) {
        // Đổi thiết bị đi qua `/auth/bind-device`, KHÔNG qua `/requests`:
        // server cần biết chính `device_id` của máy đang gửi.
        const r = await xinGanThietBi(layDeviceId(), lyDo.trim())
        tb.bao_xong(
          r.pending ? 'Đã gửi yêu cầu đổi máy' : 'Điện thoại này đã được đăng ký',
          r.pending
            ? 'Quản lý sẽ duyệt. Sau khi đổi, hệ thống khoá đổi tiếp 90 ngày.'
            : undefined,
        )
      } else {
        await guiDon({
          client_uuid: clientUuid,
          ma_server: loai.ma_server!,
          work_date: ngayLienQuan,
          reason: lyDo.trim(),
          punch_at: loai.can_gio ? `${ngayLienQuan}T${gioDeNghi}:00+07:00` : null,
          direction: loai.can_gio ? chieu : null,
          location_id: diaDiemId ? Number(diaDiemId) : null,
          record_id: loai.can_ban_ghi ? Number(banGhiId) : null,
        })
        tb.bao_xong('Đã gửi đơn',
                    'Quản lý sẽ kiểm tra. Xem kết quả ở tab Lịch sử tạo đơn từ.')
      }
      setDaGui(true)
      xong()
    } catch (err) {
      tb.bao_loi(err, 'Chưa gửi được đơn')
    } finally {
      setDangGui(false)
    }
  }

  return (
    <form className="man-hinh form-yeu-cau" onSubmit={(e) => void gui(e)}>
      <button type="button" className="nut-quay-lai" onClick={quayLai}>
        <Icon ten="quay-lai" size={18} /> Quay lại
      </button>

      <h2 style={{ fontSize: 'var(--cy-trang)' }}>{loai.nhan}</h2>
      <p className="mo-ta-trang">{loai.mo_ta}</p>

      {!laDoiThietBi && (
        <label className={`truong ${loiTruong.ngay ? 'sai' : ''}`}>
          <span>Ngày liên quan</span>
          <input
            type="date"
            value={ngayLienQuan}
            max={homNay}
            onChange={(e) => { setNgayLienQuan(e.target.value); setBanGhiId('') }}
            required
          />
          {loiTruong.ngay && <span className="truong-loi">{loiTruong.ngay}</span>}
        </label>
      )}

      {loai.can_gio && !laDoiThietBi && (
        <>
          <label className={`truong ${loiTruong.gio ? 'sai' : ''}`}>
            <span>Giờ đề nghị (giờ Việt Nam)</span>
            <input type="time" value={gioDeNghi}
                   onChange={(e) => setGioDeNghi(e.target.value)} required />
            {loiTruong.gio && <span className="truong-loi">{loiTruong.gio}</span>}
          </label>

          <label className="truong">
            <span>Chiều chấm công</span>
            <select value={chieu}
                    onChange={(e) => setChieu(e.target.value as 'in' | 'out')}>
              <option value="in">Vào ca</option>
              <option value="out">Ra ca</option>
            </select>
          </label>
        </>
      )}

      {loai.can_ban_ghi && (
        <label className={`truong ${loiTruong.banGhi ? 'sai' : ''}`}>
          <span>Bản ghi cần đính chính</span>
          <select value={banGhiId} onChange={(e) => setBanGhiId(e.target.value)} required>
            <option value="">
              {banGhi.length === 0 ? 'Không có bản ghi nào trong ngày này' : 'Chọn bản ghi'}
            </option>
            {banGhi.map((r) => (
              <option key={r.id} value={r.id}>
                #{r.id} · {gio(r.punch_at)} · {r.direction === 'in' ? 'Vào' : 'Ra'}
              </option>
            ))}
          </select>
          {loiTruong.banGhi
            ? <span className="truong-loi">{loiTruong.banGhi}</span>
            : banGhi.length === 0 && (
                <span className="canh-bao-nho">
                  Ngày này không có bản ghi nào để đính chính. Nếu bạn quên chấm
                  công, hãy dùng <strong>Đơn checkin/out</strong>.
                </span>
              )}
        </label>
      )}

      {!laDoiThietBi && (
        <label className="truong">
          <span>Địa điểm</span>
          <select value={diaDiemId} onChange={(e) => setDiaDiemId(e.target.value)}>
            <option value="">Chưa xác định — mô tả trong lý do</option>
            {diaDiem.map((l) => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
        </label>
      )}

      <label className={`truong ${loiTruong.lyDo ? 'sai' : ''}`}>
        <span>Lý do</span>
        <textarea
          value={lyDo}
          onChange={(e) => { setLyDo(e.target.value); setLoiTruong((l) => ({ ...l, lyDo: '' })) }}
          rows={4}
          required
          placeholder="Mô tả ngắn gọn để quản lý hiểu và xử lý nhanh"
        />
        {loiTruong.lyDo && <span className="truong-loi">{loiTruong.lyDo}</span>}
      </label>

      {laDoiThietBi && (
        <p className="canh-bao-nho">
          Sau khi quản lý duyệt, hệ thống <strong>khoá đổi máy tiếp 90 ngày</strong>.
          Đây là cơ chế chống mượn máy luân phiên, không phải lỗi.
        </p>
      )}

      <button type="submit" className="nut-gui" disabled={dangGui || daGui}>
        {dangGui ? 'Đang gửi…' : daGui ? 'Đã gửi' : 'Gửi đơn'}
      </button>
    </form>
  )
}
